import { motion } from 'framer-motion';
import { GraduationCap, Calendar } from 'lucide-react';
import { education } from '../data/education';

const Education = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section
      id="education"
      className="bg-gradient-to-b from-black to-gray-900 px-4 py-20 sm:px-6 lg:px-8"
    >
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 className="mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-4xl font-bold text-transparent sm:text-5xl">
            Education
          </h2>
          <div className="mx-auto h-1 w-24 bg-gradient-to-r from-blue-500 to-red-500"></div>
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute bottom-0 left-8 top-0 w-0.5 transform bg-gradient-to-b from-blue-500 via-red-500 to-blue-500 md:left-1/2 md:-translate-x-1/2"></div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-12"
          >
            {education.map((edu, index) => (
              <motion.div
                key={edu.id}
                variants={itemVariants}
                className={`relative flex items-start ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline dot */}
                <div className="absolute left-8 z-10 h-4 w-4 transform rounded-full border-4 border-black bg-red-500 md:left-1/2 md:-translate-x-1/2"></div>

                {/* Content card */}
                <div
                  className={`ml-16 w-full md:ml-0 md:w-5/12 ${
                    index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                  }`}
                >
                  <motion.div
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="rounded-xl border border-white/10 bg-white/5 p-6 backdrop-blur-md transition-all duration-300 hover:bg-white/10"
                  >
                    <div className="mb-4 flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="mb-1 text-xl font-bold text-white">{edu.degree}</h3>
                        <p className="font-semibold text-red-400">{edu.institution}</p>
                        <p className="mt-1 text-sm text-gray-400">{edu.field}</p>
                        {edu.grade && (
                          <p className="mt-2 text-sm font-semibold text-blue-400">{edu.grade}</p>
                        )}
                      </div>
                      <GraduationCap className="h-6 w-6 text-gray-400" />
                    </div>

                    <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{edu.period}</span>
                      </div>
                    </div>

                    {edu.activities && (
                      <div className="mb-4">
                        <p className="mb-2 text-sm font-medium text-gray-300">Activities:</p>
                        <p className="text-sm text-gray-400">{edu.activities}</p>
                      </div>
                    )}

                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-300">Skills:</p>
                      <div className="flex flex-wrap gap-2">
                        {edu.skills.map((skill, idx) => (
                          <span
                            key={idx}
                            className="rounded-full border border-red-500/30 bg-red-500/20 px-2 py-1 text-xs text-red-300"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Education;
